/**
 * Readiness Evaluator
 *
 * Pure function — no DB calls. Evaluates whether an item satisfies all criteria
 * in a ReadinessDefinition given the item's structural data and accepted values.
 *
 * UI MUST use this as the single evaluation source (not compute readiness inline).
 */

import type { Item } from '../types/prioritization/lifecycle';
import type {
  ReadinessDefinition,
  ReadinessCriterion,
  FieldPopulatedCriterion,
  AttributeValuesCriterion,
  AgentSignoffCriterion,
  CriterionResult,
  ReadinessEvalResult,
  RemediationRole,
  RemediationType,
} from '../types/prioritization/readinessDefinition';

// ============================================================================
// Per-criterion evaluators
// ============================================================================

/**
 * Map from structural fieldKey to the accessor on the Item object.
 * Extend when new structural fields are added.
 */
/** Items arrive in camelCase or snake_case depending on the source; read both shapes. */
const field = (item: Item, camel: string, snake?: string): unknown => {
  const rec = item as unknown as Record<string, unknown>;
  return rec[camel] ?? (snake ? rec[snake] : undefined);
};

const FIELD_ACCESSORS: Record<string, (item: Item) => unknown> = {
  title: (i) => i.title,
  description: (i) => i.description,
  itemCategory: (i) => field(i, 'itemCategory', 'item_category'),
  altitudeLevel: (i) => field(i, 'altitudeLevel', 'altitude_level'),
  dedupeStatus: (i) => field(i, 'dedupeStatus', 'dedupe_status'),
  metaCategory: (i) => field(i, 'metaCategory', 'meta_category'),
  strategicBucket: (i) => field(i, 'strategicBucket', 'strategic_bucket'),
  parentItemId: (i) => field(i, 'parentItemId', 'parent_item_id'),
  rationale: (i) => field(i, 'rationale'),
  ownerId: (i) => field(i, 'ownerId', 'owner_id'),
  beneficiary: (i) => field(i, 'beneficiary'),
  tags: (i) => (i.tags?.length ? i.tags : null),
  /** How success is measured (items.evidence_of_success). */
  evidenceOfSuccess: (i) => field(i, 'evidenceOfSuccess', 'evidence_of_success'),
  /** Why this item, why now (items.why_now). */
  whyNow: (i) => field(i, 'whyNow', 'why_now'),
  /**
   * A size someone actually gave the item. An effort the app inferred from the
   * altitude alone ('altitude_level_inferred') is a default, not an estimate.
   */
  effortEstimate: (i) => {
    const source = field(i, 'effortSource', 'effort_source');
    if (source === 'altitude_level_inferred') return null;
    return field(i, 'effortCapacityUnits', 'effort_capacity_units');
  },
};

function isPopulated(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
}

/**
 * Infer remediation role based on the field being evaluated.
 * Items owners fix item-level fields; Cycle/Track owners fix configuration fields.
 */
function inferRemediationRole(fieldKey: string): RemediationRole {
  const cycleOwnerFields = new Set(['cycleId', 'trackId', 'cycle_id', 'track_id']);
  const frameOwnerFields = new Set(['scopeType', 'scope_type', 'scopeEntityId', 'scope_entity_id']);
  if (cycleOwnerFields.has(fieldKey)) return 'Cycle owner';
  if (frameOwnerFields.has(fieldKey)) return 'Track owner';
  return 'Item owner';
}

/**
 * Infer what kind of work resolves a failed criterion.
 * Fields that describe intent or planning context are Directional Planning signals.
 */
function inferRemediationType(fieldKey: string): RemediationType {
  const planningFields = new Set([
    'rationale', 'description', 'tags', 'beneficiary',
    'strategicBucket', 'strategic_bucket', 'metaCategory', 'meta_category',
  ]);
  if (planningFields.has(fieldKey)) return 'directional_planning';
  return 'configuration';
}

function evaluateFieldPopulated(
  criterion: FieldPopulatedCriterion,
  item: Item
): CriterionResult {
  const accessor = FIELD_ACCESSORS[criterion.fieldKey];
  let value: unknown;
  if (accessor) {
    value = accessor(item);
  } else {
    // Fallback: try camelCase and snake_case directly
    value = field(item, criterion.fieldKey, toSnakeCase(criterion.fieldKey));
  }

  const pass = isPopulated(value);
  return {
    criterion,
    pass,
    reason: pass ? undefined : `${criterion.label ?? criterion.fieldKey} is not set`,
    remediationRole: pass ? undefined : inferRemediationRole(criterion.fieldKey),
    remediationType: pass ? undefined : inferRemediationType(criterion.fieldKey),
  };
}

function evaluateAttributeValues(
  criterion: AttributeValuesCriterion,
  acceptedValues: Record<string, unknown>
): CriterionResult {
  const value = acceptedValues[criterion.attributeKey];
  if (!isPopulated(value)) {
    return {
      criterion,
      pass: false,
      reason: `${criterion.label ?? criterion.attributeKey} has no accepted value`,
    };
  }

  if (criterion.requiredValues && criterion.requiredValues.length > 0) {
    const stringVal = String(value);
    if (!criterion.requiredValues.includes(stringVal)) {
      return {
        criterion,
        pass: false,
        reason: `${criterion.label ?? criterion.attributeKey} must be one of: ${criterion.requiredValues.join(', ')} (current: ${stringVal})`,
        remediationRole: 'Item owner',
        remediationType: 'directional_planning',
      };
    }
  }

  return { criterion, pass: true };
}

function evaluateAgentSignoff(
  criterion: AgentSignoffCriterion,
  agentSignoffs: Record<string, boolean>
): CriterionResult {
  const signed = agentSignoffs[criterion.agentKey] === true;
  return {
    criterion,
    pass: signed,
    reason: signed
      ? undefined
      : `${criterion.label ?? criterion.agentKey} has not signed off on this item`,
    remediationRole: signed ? undefined : 'Item owner',
    remediationType: signed ? undefined : 'directional_planning',
  };
}

// ============================================================================
// Main evaluator
// ============================================================================

/**
 * Evaluate whether an item satisfies all criteria in a readiness definition.
 *
 * @param item - The item to evaluate
 * @param definition - The readiness definition to evaluate against
 * @param acceptedValues - Map of attributeKey → accepted value (from valueProposalStore)
 * @param agentSignoffs - Map of agentKey → signed (true/false); defaults to all false
 */
export function evaluateItemReadiness(
  item: Item,
  definition: ReadinessDefinition,
  acceptedValues: Record<string, unknown> = {},
  agentSignoffs: Record<string, boolean> = {}
): ReadinessEvalResult {
  const criteriaResults: CriterionResult[] = definition.criteria.map(
    (criterion: ReadinessCriterion): CriterionResult => {
      switch (criterion.type) {
        case 'field_populated':
          return evaluateFieldPopulated(criterion, item);
        case 'attribute_values':
          return evaluateAttributeValues(criterion, acceptedValues);
        case 'agent_signoff':
          return evaluateAgentSignoff(criterion, agentSignoffs);
        default:
          return { criterion, pass: false, reason: 'Unknown criterion type' };
      }
    }
  );

  const passCount = criteriaResults.filter((r) => r.pass).length;
  const totalCount = criteriaResults.length;
  const pass = totalCount === 0 || passCount === totalCount;

  return {
    itemId: item.id,
    definitionId: definition.id,
    pass,
    criteria: criteriaResults,
    passCount,
    totalCount,
  };
}

/**
 * Evaluate all items against a definition in a single pass.
 * Returns a map of itemId → ReadinessEvalResult.
 */
export function evaluateItemsReadiness(
  items: Item[],
  definition: ReadinessDefinition,
  allAcceptedValues: Record<string, Record<string, unknown>>,
  allAgentSignoffs: Record<string, Record<string, boolean>> = {}
): Record<string, ReadinessEvalResult> {
  const results: Record<string, ReadinessEvalResult> = {};
  for (const item of items) {
    const itemValues = allAcceptedValues[item.id] ?? {};
    const itemSignoffs = allAgentSignoffs[item.id] ?? {};
    results[item.id] = evaluateItemReadiness(item, definition, itemValues, itemSignoffs);
  }
  return results;
}

// ── Utilities ─────────────────────────────────────────────────────────────────

function toSnakeCase(camel: string): string {
  return camel.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}
